import { I18n } from "@lingui/core";
import { t } from "@lingui/macro";
import {
  FailureApi,
  IApiEpConfigBillingPlan,
  IApiEpNotificationMediumType,
  IApiEpNotificationSettingsType,
  IApiEpOrganizationMembership,
  IApiEpOrganizationRoleCodename,
  IApiEpUserAuthorized,
} from "@smp/api";

export const epIsOrganizationContextUnavailable = (
  failure: FailureApi,
): boolean =>
  failure.code === "API__EP__ORGANIZATION__ORGANIZATION_NOT_FOUND" ||
  failure.code === "API__EP__ORGANIZATION__ORGANIZATION_TEMPORARILY_INACTIVE";

export const epNotificationMediumNameByIdGet = (
  id: IApiEpNotificationMediumType,
  i18n: I18n,
) => {
  const hash: { [key in IApiEpNotificationMediumType]: string } = {
    email: i18n._(t`Email`),
    gcm: i18n._(t`Android (gcm)`),
    pushbullet: i18n._(t`Pushbullet`),
    site_message: i18n._(t`Site message`),
    telegram: i18n._(t`Telegram`),
  };
  return hash[id];
};

export const epNotificationTypeNameByIdGet = (
  id: IApiEpNotificationSettingsType,
  i18n: I18n,
) => {
  const hash: {
    [key in IApiEpNotificationSettingsType | "special_service_notices"]: string;
  } = {
    account_error: i18n._(t`Account errors`),
    billing: i18n._(t`Billing`),
    comment_error: i18n._(t`Comments errors`),
    minor_update: i18n._(t`Small updates`),
    news: i18n._(t`Service news`),
    organization_event: i18n._(t`Team events`),
    partner: i18n._(t`Promo pages and partners`),
    post_error: i18n._(t`Posts errors`),
    publishing: i18n._(t`Posts published`),
    security: i18n._(t`Security notifications`),
    special_service_notices: i18n._(t`Special service notices`),
    status: i18n._(t`Service status`),
  };
  return hash[id];
};

export function* epPlanUpgradeGenerator(
  plan: IApiEpConfigBillingPlan,
  planByIdMap: Partial<
    Record<IApiEpConfigBillingPlan["id"], IApiEpConfigBillingPlan>
  >,
) {
  let next: IApiEpConfigBillingPlan | null | undefined =
    plan && plan.upgrade && planByIdMap[plan.upgrade];
  while (next) {
    yield next;
    next = next.upgrade && planByIdMap[next.upgrade];
  }
}

export const epOrganizationRoleDescriptionByCodenameGet = (
  codename: IApiEpOrganizationRoleCodename,
  i18n: I18n,
) => {
  const descriptionByCodename: {
    [key in IApiEpOrganizationRoleCodename]: string;
  } = {
    editor_v1: i18n._(
      t`Full access to the posts section, read-only access to the Social Sets section. The right choice if the scope is to solely schedule posts, and not change any publication settings or manage Social Sets.`,
    ),
    empty_v1: i18n._(t`Permissions not specified`), // usually must be not shown
    manager_v1: i18n._(
      t`Full access to the posts and Social Sets sections. Perfect if the scope is to not only schedule posts, but also to manage publication settings and Social Sets (limited to pages available to the team).`,
    ),
    observer_v1: i18n._(
      t`Read-only access to the posts and Social Sets sections. Simply observe. Great choice if the scope is just that, without the need to create new posts or change any publication settings.`,
    ),
  };
  return descriptionByCodename[codename];
};

export const epOrganizationRoleNameByCodenameGet = (codename: string) => {
  const nameByCodename: { [key in IApiEpOrganizationRoleCodename]: string } = {
    editor_v1: t`Editor`,
    empty_v1: t`Unspecified`,
    manager_v1: t`Manager`,
    observer_v1: t`Observer`,
  };
  return nameByCodename[codename] || t`Unspecified`;
};

export const epUserGetDisplayName = ({
  user,
  alwaysWithEmail,
}: {
  user: IApiEpUserAuthorized | IApiEpOrganizationMembership["user"];
  alwaysWithEmail: boolean;
}): string => {
  if (
    user.first_name &&
    user.first_name.trim().length &&
    user.last_name &&
    user.last_name.trim().length
  ) {
    return (
      `${user.first_name.trim()} ${user.last_name.trim()}` +
      (alwaysWithEmail ? ` (${user.email})` : "")
    );
  }
  if (user.first_name && user.first_name.trim().length) {
    return user.first_name.trim() + (alwaysWithEmail ? ` (${user.email})` : "");
  }
  if (user.last_name && user.last_name.trim().length) {
    return user.last_name.trim() + (alwaysWithEmail ? ` (${user.email})` : "");
  }
  return user.email;
};
